import { FiBriefcase, FiFolder } from 'react-icons/fi';
import LogoImage from '../assets/litiumLogo.png';
import type { UserWorkspaceResponse } from '../apis/core/workspace/get/getUserWorkspace';
import { WorkspaceItem, WorkspaceListProps } from '@tw/tw-component-library';

// Mock LLM Handler - replace with actual implementation
export const mockLLMHandler = async (question: string): Promise<string> => {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 1000));

  return `This is a mock response to: "${question}". In a real implementation, this would call your LLM API.`;
};

// Default dropdown configuration (fallback mock data)
export const defaultDropdown = {
  spacing: false,
  groups: [
    {
      label: 'Personal',
      options: [
        { label: 'My Workspace', value: 'my-workspace', icon: <FiFolder /> },
        {
          label: 'Personal Projects',
          value: 'personal-projects',
          icon: <FiFolder />,
        },
      ],
    },
    {
      label: 'Collaborative',
      options: [
        {
          label: 'Project Alpha',
          value: 'project-alpha',
          icon: <FiBriefcase />,
        },
      ],
    },
  ],
  selectedValue: 'my-workspace',
  onChange: (value: string) => console.log('Selected:', value),
};

// Default Header Configuration
export const defaultHeader = {
  logo: {
    src: LogoImage,
    alt: 'Litium Logo',
    size: 'md' as const,
  },
  dropdown: defaultDropdown,
  aiPolicyButton: {
    label: 'TW Generated AI Use Policy',
    onClick: () => alert('AI Policy clicked!'),
  },
  profile: {
    picture:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
    name: 'John Doe',
    email: 'john.doe@example.com',
    variant: 'imageOnly' as const,
    size: 'small' as const,
    imageShape: 'square' as const,
    onSettingsClick: () => alert('Settings clicked!'),
    onLogoutClick: () => alert('Logout clicked!'),
  },
};

/**
 * Transforms workspace data into dropdown format for header
 * Groups workspaces by type (Personal, Collaborative, Standard)
 */
export const transformWorkspacesToDropdown = (
  workspaces: UserWorkspaceResponse,
) => {
  // Group workspaces by type
  const personalWorkspaces = workspaces.filter(
    (ws) => ws.workspaceType === 'Private',
  );
  const collaborativeWorkspaces = workspaces.filter(
    (ws) => ws.workspaceType === 'Collaborative',
  );
  const standardWorkspaces = workspaces.filter(
    (ws) => ws.workspaceType === 'Standard',
  );

  const groups: Array<{
    label: string;
    options: Array<{
      label: string;
      value: string;
      icon: React.ReactElement;
    }>;
  }> = [];

  // Add Personal Workspaces group
  if (personalWorkspaces.length > 0) {
    groups.push({
      label: 'Personal',
      options: personalWorkspaces
        .sort(
          (a, b) =>
            new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime(),
        )
        .map((ws) => ({
          label: ws.title,
          value: ws.id,
          icon: <FiFolder />,
        })),
    });
  }

  // Add Collaborative Workspaces group
  if (collaborativeWorkspaces.length > 0) {
    groups.push({
      label: 'Collaborative',
      options: collaborativeWorkspaces
        .sort(
          (a, b) =>
            new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime(),
        )
        .map((ws) => ({
          label: ws.title,
          value: ws.id,
          icon: <FiBriefcase />,
        })),
    });
  }

  // Add Standard Workspaces group (if any)
  if (standardWorkspaces.length > 0) {
    groups.push({
      label: 'Standard',
      options: standardWorkspaces
        .sort(
          (a, b) =>
            new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime(),
        )
        .map((ws) => ({
          label: ws.title,
          value: ws.id,
          icon: <FiBriefcase />,
        })),
    });
  }

  return groups;
};

// Workspace Items Data (fallback mock data)
export const createWorkspaceItems = [
  {
    title: 'Personal',
    items: [
      {
        id: '1',
        title: 'My Workspace',
        lastUsed: '2 hours ago',
      },
      {
        id: '2',
        title: 'Personal Projects',
        lastUsed: '1 day ago',
      },
      {
        id: '3',
        title: 'Drafts',
        lastUsed: '3 days ago',
      },
    ],
  },
  {
    title: 'Collaborative',
    items: [
      {
        id: '4',
        title: 'Project Alpha',
        lastUsed: '5 minutes ago',
      },
      {
        id: '5',
        title: 'Project Beta',
        lastUsed: '1 hour ago',
      },
      {
        id: '6',
        title: 'Team Collaboration',
        lastUsed: '2 hours ago',
      },
    ],
  },
];

// Recent Items Data
export const recentItems = [
  {
    id: 'recent-1',
    title: 'Document Analysis',
    lastUsed: '2 minutes ago',
  },
  {
    id: 'recent-2',
    title: 'Client Meeting Notes',
    lastUsed: '15 minutes ago',
  },
  {
    id: 'recent-3',
    title: 'Contract Review',
    lastUsed: '1 hour ago',
  },
  {
    id: 'recent-4',
    title: 'Research Document',
    lastUsed: '3 hours ago',
  },
];

// Prompt Library Items Data
export const promptLibraryItems = [
  {
    id: 'prompt-1',
    title: 'Legal Document Summarizer',
    lastUsed: 'Yesterday',
  },
  {
    id: 'prompt-2',
    title: 'Contract Clause Generator',
    lastUsed: '2 days ago',
  },
  {
    id: 'prompt-3',
    title: 'Case Analysis Assistant',
    lastUsed: 'Last week',
  },
  {
    id: 'prompt-4',
    title: 'Email Draft Generator',
    lastUsed: 'Last week',
  },
  {
    id: 'prompt-5',
    title: 'Research Query Builder',
    lastUsed: '2 weeks ago',
  },
];

export const tutorials = [
  {
    id: 'getting-started',
    title: 'Getting Started with LitiumTW',
    description: 'Learn the basics of using LitiumTW',
    difficulty: 'beginner',
    estimatedTime: '10 minutes',
    keywords: ['getting started', 'beginner', 'basics', 'tutorial', 'learn'],
    steps: [
      {
        id: 'step-1',
        title: 'Welcome',
        content: "Welcome to LitiumTW! Let's get you started.",
        instructions: [
          'Navigate to your workspace',
          'Create your first project',
          'Explore the dashboard',
        ],
      },
      {
        id: 'step-2',
        title: 'Create a Workspace',
        content: 'Create your first workspace to organize your projects.',
        codeExample: '// Example code here',
        action: {
          label: 'Create Workspace',
          route: '/workspace/create',
        },
      },
    ],
  },
];

/**
 * Guided, on-page interactive tutorials that spotlight specific UI elements.
 * These reuse the same Tutorial shape but rely on `targetSelector` for each step,
 * which is consumed by the QuestionAssistant overlay tour.
 */
export const guidedTutorials = [
  {
    id: 'question-assistant-guide',
    title: 'Question Assistant Guide',
    description:
      'Step-by-step guide for asking questions, using Chat, and reading results.',
    difficulty: 'beginner' as const,
    estimatedTime: '3 minutes',
    keywords: ['guide', 'question assistant', 'help', 'how to ask'],
    steps: [
      {
        id: 'qa-guide-step-1',
        title: 'Question Assistant area',
        content:
          'This is the main Question Assistant section where you can ask LitiumTW for help.',
        instructions: [
          'You will use this panel whenever you want guidance or suggestions.',
          'This is not an Ask session, it is a Question Assistant session.',
        ],
        // Landing page wrapper around the QuestionAssistant
        targetSelector: "[data-tour='lp-question-assistant-section']",
      },
      {
        id: 'qa-guide-step-2',
        title: 'Ask your question',
        content:
          'Type your question here — for example, “Help me create a new workspace”.',
        instructions: [
          'Describe what you want to achieve in plain language.',
          'You can use multiple lines if needed.',
        ],
        // QuestionAssistant textarea input
        targetSelector: "[data-tour='qa-input']",
      },
      {
        id: 'qa-guide-step-3',
        title: 'Submit your question',
        content:
          'Click the Submit button to send your question and see suggested next steps.',
        instructions: [
          'You can also use Ctrl+Enter / Cmd+Enter as a shortcut.',
        ],
        // Primary submit button inside QuestionAssistant
        targetSelector: "[data-tour='qa-submit-button']",
      },
      {
        id: 'qa-guide-step-4',
        title: 'Open a full Chat session',
        content:
          'Use the Chat button to open a richer Ask session in your private workspace.',
        instructions: [
          'This is useful when you want a longer back-and-forth conversation.',
        ],
        // Secondary "Chat" button inside QuestionAssistant
        targetSelector: "[data-tour='qa-secondary-button']",
      },
      {
        id: 'qa-guide-step-5',
        title: 'Review answers and actions',
        content:
          'Your answers and suggested actions appear here after submitting a question.',
        instructions: [
          'Review the explanation.',
          'Use the suggested actions below to quickly jump into workspaces or tasks.',
        ],
        // Answers block beneath the QuestionAssistant input
        targetSelector: "[data-tour='qa-answers-block']",
      },
    ],
  },
  {
    id: 'create-non-client-workspace-guide',
    title: 'Create a Non-Matter Workspace',
    description:
      'Guided walkthrough for creating a non-client matter workspace and adding collaborators.',
    difficulty: 'beginner' as const,
    estimatedTime: '5 minutes',
    defaultMode: 'interactive',
    keywords: [
      'guide',
      'workspace',
      'create workspace',
      'non-matter',
      'collaborative',
    ],
    steps: [
      {
        id: 'create-non-client-step-1',
        title: 'Open the workspace creator',
        content:
          'Start by opening the workspace creation flow from the landing page.',
        disableNextButton: true,
        autoAdvanceOnTargetClick: true,
        instructions: [
          'Locate the "Create a New Workspace" panel on the landing page.',
          'Click the "Create a New Workspace" button in this panel to open the workspace creator.',
        ],
        targetSelector: "[data-tour='lp-create-workspace-list']",
      },
      {
        id: 'create-non-client-step-2',
        title: 'Choose Non-Matter Workspace and continue',
        content:
          'Select the Non-Matter option to create a workspace that is not tied to a client file.',
        tooltipPlacement: 'top',
        disableNextButton: true,
        autoAdvanceOnTargetClick: true,
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-workspace-modal-close-button']",
        },
        instructions: [
          'In the "Select Workspace Type" step, click the "Non-Matter Workspace" card.',
          'Make sure the Non-Matter card is highlighted to confirm your choice.',
          'When you are ready, click "Continue" at the bottom of the modal to move to the next step.',
        ],
        targetSelector: "[data-tour='create-ws-type-non-matter']",
      },
      {
        id: 'create-non-client-step-3',
        title: 'Once a type has been selected, press continue',
        content:
          'Once a type has been selected, press continue to move to the next step.',
        tooltipPlacement: 'top',
        disableNextButton: true,
        autoAdvanceOnTargetClick: true,
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        instructions: [
          'Once a type has been selected, press continue to move to the next step.',
        ],
        targetSelector: "[data-tour='create-ws-continue-button']",
      },
      {
        id: 'create-non-client-step-4',
        title: 'Enter workspace name and description',
        content:
          'Give your workspace a clear name and description so your team understands its purpose.',
        tooltipPlacement: 'top',
        disableNextButton: false,
        // Don’t advance just by clicking the form area
        autoAdvanceOnTargetClick: false,
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        // Advance when the user clicks Continue, but only if the fields are filled
        autoAdvance: {
          require: {
            // Only gate on the two fields on this step.
            filledSelectors: [
              "[data-tour='create-ws-details-name-desc'] input",
            ],
          },
        },
        // Also disable Next/Finish until the fields are filled
        validation: {
          filledSelectors: ["[data-tour='create-ws-details-name-desc'] input"],
        },
        instructions: [
          'Enter a descriptive Workspace Name (for example, "Internal Research Workspace").',
          'Add a short Description explaining what this workspace will be used for.',
        ],
        targetSelector: "[data-tour='create-ws-details-name-desc']",
      },
      {
        id: 'create-non-client-step-5',
        title: 'Once workspace data has been entered, press continue',
        content:
          'Once workspace data has been entered, press continue to move to the next step.',
        tooltipPlacement: 'top',
        disableNextButton: true,
        autoAdvanceOnTargetClick: true,
        instructions: [
          'Once workspace data has been entered, press continue to move to the next step.',
        ],
        targetSelector: "[data-tour='create-ws-continue-button']",
      },
      {
        id: 'create-non-client-step-6',
        title: 'Add collaborators (optional)',
        content:
          'Add teammates to make this workspace collaborative, or skip this step to keep it just for you.',
        tooltipPlacement: 'top',
        autoAdvanceOnTargetClick: false,
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        autoAdvance: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-continue-button']",
        },
        instructions: [
          'In "Add Members", start typing a colleague’s name or email to search for them.',
          'Select the user from the suggestions list.',
          'Click "Add" to include them in the workspace.',
          'Repeat for any additional collaborators you want to include.',
        ],
        targetSelector: "[data-tour='create-ws-members-step']",
      },
      {
        id: 'create-non-client-step-7',
        title: 'Once workspace data has been entered, press continue',
        content:
          'Once collaborators have been added, press continue to move to the next step.',
        tooltipPlacement: 'top',
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        instructions: [
          'Once collaborators have been added, press continue to move to the next step.',
        ],
        targetSelector: "[data-tour='create-ws-continue-button']",
      },
      {
        id: 'create-non-client-step-8',
        title: 'Review and create your workspace',
        content:
          'Review your workspace and finish the guided tour by creating it.',
        tooltipPlacement: 'top',
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        instructions: [
          'On the Review step, check the workspace type, name, description, and members to confirm everything looks correct.',
          'This is the end of the guided tour: when you click "Create Workspace" you will be directed into your new workspace.',
        ],
        targetSelector: "[data-tour='create-ws-review-section']",
      },
      {
        id: 'create-non-client-step-9',
        title:
          'Once you have created your workspace, you will be directed into your new workspace.',
        content:
          'Once you have created your workspace, you will be directed into your new workspace.',
        tooltipPlacement: 'top',
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        instructions: [
          'This is the end of the guided tour: when you click "Create Workspace" you will be directed into your new workspace.',
        ],
        targetSelector: "[data-tour='create-ws-submit-button']",
      },
    ],
  },
  {
    id: 'create-matter-workspace-guide',
    title: 'Create a Matter Workspace',
    description: 'Guided walkthrough for creating a client-matter workspace.',
    difficulty: 'beginner' as const,
    estimatedTime: '6 minutes',
    defaultMode: 'interactive',
    keywords: ['guide', 'workspace', 'matter', 'client-matter'],
    steps: [
      {
        id: 'create-matter-step-1',
        title: 'Open the workspace creator',
        content:
          'Start by opening the workspace creation flow from the landing page.',
        disableNextButton: true,
        instructions: [
          'Locate the "Create a New Workspace" panel on the landing page.',
          'Click the "Create a New Workspace" button in this panel to open the workspace creator.',
        ],
        targetSelector: "[data-tour='lp-create-workspace-list']",
      },
      {
        id: 'create-matter-step-2',
        title: 'Choose Client-Matter and continue',
        content:
          'Select the Client-Matter option to create a workspace tied to a client matter.',
        tooltipPlacement: 'top',
        disableNextButton: true,
        autoAdvanceOnTargetClick: true,
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-workspace-modal-close-button']",
        },
        instructions: [
          'In the "Select Workspace Type" step, click the "Client-Matter Workspace" card.',
          'Make sure the Client-Matter card is highlighted to confirm your choice.',
        ],
        targetSelector: "[data-tour='create-ws-type-client-matter']",
      },
      {
        id: 'create-matter-step-3',
        title: 'Press continue',
        content:
          'Once the type has been selected, press continue to move forward.',
        tooltipPlacement: 'top',
        disableNextButton: true,
        autoAdvanceOnTargetClick: true,
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        instructions: ['Press continue to move to the next step.'],
        targetSelector: "[data-tour='create-ws-continue-button']",
      },
      {
        id: 'create-matter-step-4',
        title: 'Enter workspace name and description',
        content:
          'Give your workspace a clear name and description so your team understands what it’s for.',
        tooltipPlacement: 'top',
        disableNextButton: false,
        autoAdvanceOnTargetClick: false,
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        autoAdvance: {
          require: {
            filledSelectors: [
              "[data-tour='create-ws-details-name-desc'] input",
            ],
          },
        },
        validation: {
          filledSelectors: ["[data-tour='create-ws-details-name-desc'] input"],
        },
        instructions: [
          'Enter a descriptive Workspace Name (this is saved for the next tutorial).',
          'Add a short Description explaining the workspace purpose.',
        ],
        targetSelector: "[data-tour='create-ws-details-name-desc']",
      },
      {
        id: 'create-matter-step-5',
        title: 'Press continue',
        content:
          'Once you’ve entered the workspace details, press continue to move to the next step.',
        tooltipPlacement: 'top',
        disableNextButton: true,
        autoAdvanceOnTargetClick: true,
        instructions: ['Press continue to move to the next step.'],
        targetSelector: "[data-tour='create-ws-continue-button']",
      },
      {
        id: 'create-matter-step-6',
        title: 'Add collaborators (optional)',
        content:
          'Add teammates to make this workspace collaborative, or skip this step.',
        tooltipPlacement: 'top',
        autoAdvanceOnTargetClick: false,
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        autoAdvance: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-continue-button']",
        },
        instructions: [
          'In "Add Members", start typing a colleague’s name or email.',
          'Select a user from the suggestions list.',
          'Click "Add" to include them (repeat if needed).',
        ],
        targetSelector: "[data-tour='create-ws-members-step']",
      },
      {
        id: 'create-matter-step-7',
        title: 'Press continue',
        content:
          'Once collaborators have been added (or skipped), press continue to move to review.',
        tooltipPlacement: 'top',
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        instructions: ['Press continue to move to the next step.'],
        targetSelector: "[data-tour='create-ws-continue-button']",
      },
      {
        id: 'create-matter-step-8',
        title: 'Review and create your workspace',
        content:
          'Review the workspace details and finish the guided tour by creating it.',
        tooltipPlacement: 'top',
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        instructions: [
          'On the Review step, check the workspace type, name, description, and members.',
          'When you click "Create Workspace", you’ll be directed into your new workspace.',
        ],
        targetSelector: "[data-tour='create-ws-review-section']",
      },
      {
        id: 'create-matter-step-9',
        title: 'Finish creation',
        content: 'Click "Create Workspace" to enter your new matter workspace.',
        tooltipPlacement: 'top',
        previousAction: {
          trigger: 'selectorClick',
          selector: "[data-tour='create-ws-back-button']",
        },
        instructions: ['This is the end of the guided tour.'],
        targetSelector: "[data-tour='create-ws-submit-button']",
      },
    ],
  },
  {
    id: 'ask-in-matter-workspace-guide',
    title: 'Create an Ask + Link a Document',
    description:
      'Open the Ask Hub, start a new Ask session with the default persona, upload a document, and send a prompt using it.',
    difficulty: 'beginner' as const,
    estimatedTime: '8-12 minutes',
    defaultMode: 'interactive',
    keywords: ['guide', 'ask', 'documents', 'persona', 'upload'],
    steps: (() => {
      const steps = [
        {
          title: 'Switch workspace to Standard',
          content:
            'The workspace you just created is Standard. Make sure you are viewing Standard workspaces.',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          instructions: [
            'On the landing page, use the workspace tabs (Standard/Private).',
            'Continue once you can see your workspace list.',
          ],
          targetSelector: "[data-tour='lp-create-workspace-list-tabs']",
        },
        {
          title: 'Open the workspace you created',
          content: 'Click the workspace created in the previous tutorial.',
          tooltipPlacement: 'top',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          additionalData: {
            additionalButton: {
              label: 'Private',
              variant: 'outlineHoverAlt',
              advanceAfterAction: true,
              action: {
                trigger: 'selectorClick',
                selector: "[data-tour='lp-workspace-list-listless-button']",
              },
            },
          },
          instructions: [
            'Find and click the workspace you created in the previous guide from the list.',
            'If needed, use the Private button in this step, then click the workspace from that list.',
          ],
          targetSelector: "[data-tour='lp-workspace-list-group']",
        },
        {
          title: 'Go to Ask',
          content: 'On the workspace page, click “Ask” to open Ask Hub.',
          tooltipPlacement: 'top',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          instructions: [
            'Please wait for the workspace to load, sometimes it can take a few seconds.',
            'Click the “Ask” card.',
          ],
          targetSelector: "[data-tour='workspace-page-ask-card']",
        },
        {
          title: 'Start a new Ask session',
          content: 'Click “+ New Ask Session”.',
          tooltipPlacement: 'top',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          instructions: ['This opens a persona + session creation model.'],
          targetSelector: "[data-tour='askhub-new-session-button']",
        },
        {
          title: 'Choose a persona',
          tooltipPlacement: 'top',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          content:
            'This model lets you pick the persona for the new Ask session.',
          instructions: [
            'Read the persona options and select the default persona next.',
          ],
          targetSelector: "[data-tour='ask-persona-modal']",
        },
        {
          title: 'Submit persona',
          content: 'Click “Submit” to create the session.',
          tooltipPlacement: 'top',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          instructions: ['This opens your Ask session.'],
          targetSelector: "[data-tour='ask-persona-submit-button']",
        },
        {
          title: 'Chat',
          content: 'This is the main chat timeline for the Ask session.',
          blockTargetClicks: true,
          instructions: ['Messages from you and the assistant appear here.'],
          targetSelector: "[data-tour='ask-session-chat']",
        },
        {
          title: 'Chat input',
          content: 'Type your prompt here (use # for linked documents).',
          tooltipPlacement: 'right',
          blockTargetClicks: true,
          instructions: ['Click the input.'],
          targetSelector: "[data-tour='ask-session-chat-input']",
        },
        {
          title: 'Context panel',
          tooltipPlacement: 'left',
          content:
            'This right-side panel shows the thread summary and your active documents.',
          blockTargetClicks: true,
          instructions: ['You can expand/collapse Active Documents.'],
          targetSelector: "[data-tour='ask-session-context-panel']",
        },
        {
          title: 'Active Documents',
          tooltipPlacement: 'left',
          autoAdvanceOnTargetClick: false,
          content:
            'These are the documents available for linking (and for # insertion).',
          blockTargetClicks: true,
          instructions: ['Linked documents show a “💬 Linked” badge.'],
          targetSelector: "[data-tour='ask-session-active-documents']",
        },
        {
          title: 'Keyboard shortcuts',
          tooltipPlacement: 'left',
          content:
            'Use the shortcuts to move faster while writing and searching.',
          blockTargetClicks: true,
          instructions: ['Open the accordion if needed to review them.'],
          targetSelector: "[data-tour='ask-session-keyboard-shortcuts']",
        },
        {
          title: 'Add a document',
          content: 'Let’s add a document to this Ask session.',
          tooltipPlacement: 'left',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          instructions: ['Click “+ Add Document”.'],
          targetSelector: "[data-tour='ask-session-add-document-button']",
        },
        {
          title: 'Select Documents modal',
          tooltipPlacement: 'top',
          content: 'Choose which documents to link to this Ask session.',
          disableNextButton: false,
          autoAdvanceOnTargetClick: false,
          blockTargetClicks: true,
          instructions: [
            'Use can “Type to search” to find existing documents or upload new ones.',
            'But in this tutorial we will upload a new one, please click next to continue.',
          ],
          targetSelector: "[data-tour='doc-selector-select-documents-modal']",
        },
        {
          title: 'Upload Documents',
          content:
            'Upload a document if you don’t already have it in the workspace.',
          tooltipPlacement: 'top',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          instructions: ['Click “Upload Documents” to open the upload dialog.'],
          targetSelector: "[data-tour='doc-selector-upload-documents-button']",
        },
        {
          title: 'Choose files',
          tooltipPlacement: 'top',
          content:
            'This is the upload dialog. Drag & drop or select a file to upload.',
          disableNextButton: false,
          autoAdvanceOnTargetClick: false,
          autoAdvanceWhenValid: false,
          // validation: {
          //   filledSelectors: [
          //     "[data-tour='doc-selector-upload-file-input-selected-file']",
          //   ],
          // },
          instructions: [
            'Select a file using Click or drag files to upload.',
            'You can choose more than one document, but for this tutorial we will only upload one.',
            'This step will continue automatically once a file is selected.',
          ],
          targetSelector: "[data-tour='doc-selector-upload-file-input-area']",
        },
        {
          title: 'This is your uploaded document',
          tooltipPlacement: 'top',
          content:
            'Your selected file appears here. Review it before continuing.',
          blockTargetClicks: true,
          instructions: ['Press Next to continue to versioning options.'],
          targetSelector:
            "[data-tour='doc-selector-upload-file-input-selected-file']",
        },
        {
          title: 'Version Files if they already exist',
          tooltipPlacement: 'top',
          content: 'If the file already exists, choose whether to version it.',
          blockTargetClicks: true,
          instructions: [
            'Leave the default unless you specifically want versioning behavior.',
          ],
          targetSelector:
            "[data-tour='doc-selector-version-files-checkbox-label']",
        },
        {
          title: 'Upload Files',
          content: 'Click “Upload Files” to start uploading.',
          tooltipPlacement: 'top',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          instructions: ['Wait for the upload dialog to close.'],
          targetSelector: "[data-tour='doc-selector-upload-files-button']",
        },
        {
          title: 'Wait for processing',
          content: 'The backend is uploading and processing your document(s).',
          blockTargetClicks: true,
          instructions: [
            'Once the document has been uploaded successfully you should see the below message',
            '“Uploading … Waiting for files to be processed.”',
          ],
          targetSelector: "[data-tour='doc-selector-uploading-indicator']",
        },
        {
          title: 'You can keep using the UI',
          content:
            'The processing happens in the background. You can review what’s selected while it processes.',
          tooltipPlacement: 'top',
          blockTargetClicks: true,
          instructions: [
            'You can click the cancel and carry on with working whilst it processes',
            'But for the sake of this tutorial, we will wait.',
            'Please press next to continue.',
          ],
          targetSelector: "[data-tour='doc-selector-cancel-button']",
        },
        {
          title: 'Linked status',
          content:
            'When the processing is complete, it will report the file has been added to the workspace, it will appear with “💬 Linked”.',
          blockTargetClicks: true,
          // Keep Next enabled so the UI-level observer can advance this step
          // automatically as soon as the linked badge appears.
          disableNextButton: false,
          instructions: [
            'This can take a few minutes to refresh and show the linked status, please wait for it to complete.',
            'The reason why this can take a few minutes is because the document is being processed by the backend and it is not instant.',
            'Find the uploaded document row that now shows the Linked badge.',
          ],
          targetSelector: "[data-tour='doc-selector-linked-badge']",
        },
        {
          title: 'Add Documents',
          content: 'Click “Add Documents” to link the selected document(s).',
          tooltipPlacement: 'top',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          instructions: ['This will add the document(s) to Active Documents.'],
          targetSelector: "[data-tour='doc-selector-add-documents-button']",
        },
        {
          title: 'Linked document is active',
          content:
            'You should now see the newly linked document in Active Documents.',
          tooltipPlacement: 'left',
          blockTargetClicks: true,
          instructions: [
            'Look for the Linked badge in the active document list.',
          ],
          targetSelector:
            "[data-tour='ask-session-active-document-linked-badge']",
        },
        {
          title: 'Send a prompt referencing the document',
          content:
            'Use the # key to reference the linked document(s) in your prompt.',
          tooltipPlacement: 'top',
          blockKeyboardPress: true,
          disableNextButton: false,
          autoAdvanceOnTargetClick: false,
          instructions: [
            'Click the chat input and type something like: “Please summarise #.',
            'Once your message is ready, please click next',
            'Whilst not in the tutorial, you would be able to press the enter key to send a message, but within the tutorial, it has been blocked',
          ],
          targetSelector: "[data-tour='ask-session-chat-input']",
        },
        {
          title: 'Select a linked document from the popup',
          content:
            'When you press #, a popup appears with your linked documents to choose from.',
          tooltipPlacement: 'top',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          instructions: [
            'Documents linked will appear here for selecting.',
            'Please select the document.',
          ],
          targetSelector: "[data-tour='ask-session-context-menu']",
        },
        {
          title: 'Submit',
          content: 'Send your prompt.',
          tooltipPlacement: 'top',
          disableNextButton: true,
          autoAdvanceOnTargetClick: true,
          instructions: ['Press Send (or Enter) to submit, This will end the tutorial and the assistant will respond with the summarised document.'],
          targetSelector: "[data-tour='ask-session-send-button']",
        },
        {
          title: 'Finished',
          content: 'Finished this tutorial',
          tooltipPlacement: 'center',
        },
      ];

      // Auto-generate stable numeric IDs (matter-ask-step-1..N)
      // to avoid manual numbering mistakes when inserting/removing steps.
      return steps.map((step, idx) => ({
        ...step,
        id: `matter-ask-step-${idx + 1}`,
      }));
    })(),
  },
];

// Complete Landing Page Mock Data
export const landingPageMockData = {
  header: defaultHeader,
  questionAssistant: {
    title: 'What would you like to achieve using LitiumTW?',
    placeholder: 'Type your question here...',
    submitButtonText: 'Submit',
    enableKeywordExtraction: false,
    onQuestionSubmit: mockLLMHandler,
    keywordActions: [
      {
        keywords: ['create', 'new', 'workspace'],
        action: 'create-workspace',
        route: '/workspace/create',
        description: 'Create a new workspace',
      },
      {
        keywords: ['view', 'recent', 'items'],
        action: 'view-recent-items',
        route: '/recent-items',
        description: 'View your recent items',
      },
      {
        keywords: ['view', 'prompt', 'library'],
        action: 'view-prompt-library',
        route: '/prompt-library',
        description: 'View your prompt library',
      },
      {
        keywords: ['help', 'support', 'documentation', 'guide'],
        action: 'help',
        route: '/help',
        description: 'Get help and documentation',
      },
      {
        keywords: ['settings', 'preferences', 'config'],
        action: 'settings',
        route: '/settings',
        description: 'Open settings',
      },
    ],
  },
  workspaceLists: [
    {
      title: 'Create a New Workspace',
      groups: createWorkspaceItems,
      enableSearch: true,
      titleAsButton: true,
      titleButtonProps: {
        variant: 'outlineHover',
      },
      searchPlaceholder: 'Search workspaces...',
      onCreateNew: () => alert('Create a New Workspace clicked!'),
    },
    {
      title: 'Your Recent Items',
      items: recentItems,
      enableSearch: false,
      onItemClick: (item: { title: string }) => alert(`Clicked: ${item.title}`),
    },
    {
      title: 'Prompt Library',
      items: promptLibraryItems,
      enableSearch: true,
      searchPlaceholder: 'Search prompts...',
      titleAsButton: true,
      titleButtonProps: {
        variant: 'outlineHover',
      },
      onCreateNew: () => alert('Prompt Library clicked!'),
      onItemClick: (item: { title: string }) =>
        alert(`Prompt clicked: ${item.title}`),
    },
  ],
};

export const workspacePageMockData: WorkspaceListProps[] = [
  {
    title: 'Recent Chats',
    items: [
      {
        id: 'ask-1',
        title: 'Ask 1',
        lastUsed: '2 minutes ago',
      },
      {
        id: 'ask-2',
        title: 'Ask 2',
        lastUsed: '15 minutes ago',
      },
    ],
    enableSearch: true,
    searchPlaceholder: 'Search chats...',
    onItemClick: (item: WorkspaceItem) =>
      alert(`Ask clicked: ${item.title} ${item.lastUsed}`),
  },
  {
    title: 'Recent Documents',
    items: [
      {
        id: 'document-1',
        title: 'Document 1',
        lastUsed: '2 minutes ago',
      },
    ],
    enableSearch: true,
    searchPlaceholder: 'Search documents...',
    onItemClick: (item: WorkspaceItem) =>
      alert(`Document clicked: ${item.title}`),
  },
];
